import { useChatEntityFeatureFlags } from "@/components/shared/chat-feature-flags-context";
import { ContentRemovedBadge } from "@/components/shared/content-removed-badge";
import { TakedownActions } from "@/components/shared/takedown-actions";
import {
  isInteractionCustomType,
  resolveChatMessageContent,
} from "@/components/shared/chat-message-content-renderer";
import type {
  ChatMessage,
  ChatRenderContext,
} from "@/components/shared/chat-message-types";
import { GifMessageRenderer } from "@/components/conversations/gif-message-renderer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { HeicImage } from "@/components/ui/heic-image";
import { MutingAudio, MutingVideo } from "@/components/shared/muting-media";
import {
  isUnsupportedAudio,
  loadPlayableAudio,
} from "@/lib/audio/repair-unplayable-audio";
import { useAudioTranscoder } from "@/lib/hooks/use-audio-transcoder";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { getDisplayText, MENTOR_LANGUAGE } from "@/lib/sendbird/translation";
import { cn, getInitials } from "@/lib/utils";
import {
  getMessageTakedown,
  isMessageHidden,
  type MessageTakedownData,
} from "@/lib/utils/parse-message-data";
import {
  CaptionsIcon,
  FileImageIcon,
  ImageIcon,
  Loader2,
  MicIcon,
  VideoIcon,
} from "lucide-react";
import { DateTime } from "luxon";
import {
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react";

interface ChatMessageItemProps {
  message: ChatMessage;
  layout: {
    alignment: "left" | "right";
    messageStyle: "incoming" | "outgoing";
    isHighlighted?: boolean;
    dimWhenHidden?: boolean;
  };
  sender: {
    label: string;
    profileUrl?: string | null;
  };
  slots?: {
    bubbleAccessories?: ReactNode;
    reactions?: ReactNode;
    metaExtra?: ReactNode;
  };
  context?: ChatRenderContext;
  behavior?: {
    onParentMessageClick?: (message: ChatMessage) => void;
    onRequestTranscript?: () => void;
  };
}

export function ChatMessageItem({
  message,
  layout,
  sender,
  slots,
  context,
  behavior,
}: ChatMessageItemProps) {
  const {
    alignment,
    messageStyle,
    isHighlighted = false,
    dimWhenHidden = false,
  } = layout;
  const isHidden = isMessageHidden(message.data);
  const takedown = getMessageTakedown(message.data);
  const entityFlags = useChatEntityFeatureFlags();
  const [locallyReversed, setLocallyReversed] = useState(false);

  if (takedown && !locallyReversed) {
    return (
      <div className="mb-4" data-sb-message-id={message.id}>
        <TakedownNotice
          takedown={takedown}
          messageId={message.id}
          conversationId={context?.conversation?.id ?? null}
          onReversed={() => setLocallyReversed(true)}
        />
      </div>
    );
  }

  const specialContent = resolveChatMessageContent({
    message,
    context,
    entityFlags,
  });

  const shouldRenderMessageBody =
    !isHidden &&
    (!isInteractionCustomType(message.customType) ||
      message.text.trim().length > 0);

  if (!isHidden && specialContent?.kind === "hidden") {
    return null;
  }

  if (!isHidden && specialContent?.kind === "centered") {
    return (
      <div className="mb-4" data-sb-message-id={message.id}>
        {specialContent.content}
      </div>
    );
  }

  const formattedTime = DateTime.fromMillis(message.createdAt).toLocaleString(
    DateTime.TIME_SIMPLE,
  );

  const preBubble =
    !isHidden && specialContent?.kind === "preBubble"
      ? specialContent.content
      : null;

  return (
    <div
      className={cn(
        "mb-4 flex items-start",
        alignment === "right" ? "justify-end" : "justify-start",
        dimWhenHidden && isHidden && "opacity-60",
      )}
      data-sb-message-id={message.id}
    >
      <div
        className={cn(
          "order-2 mx-2 flex max-w-md flex-col items-start space-y-2 text-sm",
          alignment === "right" ? "items-end" : "items-start",
        )}
      >
        {message.parentMessage && (
          <QuotedMessageCard
            message={message.parentMessage}
            onClick={
              behavior?.onParentMessageClick
                ? () => behavior.onParentMessageClick?.(message.parentMessage!)
                : undefined
            }
          />
        )}
        {preBubble}
        <div
          className={cn("flex items-center gap-2", {
            "flex-row-reverse": alignment === "right",
          })}
        >
          {isHidden ? (
            <RedactedMessage />
          ) : shouldRenderMessageBody ? (
            <MessageBody
              message={message}
              messageStyle={messageStyle}
              isHighlighted={isHighlighted}
              onRequestTranscript={behavior?.onRequestTranscript}
            />
          ) : null}
          {slots?.bubbleAccessories}
        </div>
        <div className="flex flex-row items-center gap-2">
          <div className="flex flex-row items-center">
            <Avatar className="h-5 w-5">
              <AvatarImage src={sender.profileUrl ?? undefined} />
              <AvatarFallback className="text-[8px]">
                {getInitials(sender.label)}
              </AvatarFallback>
            </Avatar>
            <span className="ml-1 text-xs font-medium">{sender.label}</span>
          </div>
          <div className="h-1 w-1 rounded-full bg-gray-600" />
          <span className="text-xs uppercase text-gray-500">
            {formattedTime}
          </span>
          {slots?.metaExtra}
        </div>
        {slots?.reactions}
      </div>
    </div>
  );
}

function MessageBody({
  message,
  messageStyle,
  isHighlighted,
  onRequestTranscript,
}: {
  message: ChatMessage;
  messageStyle: "incoming" | "outgoing";
  isHighlighted: boolean;
  onRequestTranscript?: () => void;
}) {
  const isGifMessage = message.customType === "GIF_MESSAGE";
  const isFileMessage = message.kind === "file";

  if (isGifMessage) {
    return (
      <GifMessageRenderer
        gifUrl={message.text}
        isCurrentUser={messageStyle === "outgoing"}
      />
    );
  }

  if (isFileMessage) {
    return (
      <AttachmentMessage
        message={message}
        messageStyle={messageStyle}
        onRequestTranscript={onRequestTranscript}
      />
    );
  }

  return (
    <TextMessage
      message={message}
      messageStyle={messageStyle}
      isHighlighted={isHighlighted}
    />
  );
}

function TextMessage({
  message,
  messageStyle,
  isHighlighted,
}: {
  message: ChatMessage;
  messageStyle: "incoming" | "outgoing";
  isHighlighted: boolean;
}) {
  const [showOriginal, setShowOriginal] = useState(false);
  const { text: translatedText, isTranslated } = getDisplayText(
    { message: message.text, translations: message.translations ?? {} },
    MENTOR_LANGUAGE,
  );
  const renderedText =
    isTranslated && showOriginal ? message.text : translatedText;

  const links: string[] = renderedText.match(/(https?:\/\/\S+)/g) || [];
  const textParts: string[] = renderedText.split(/(https?:\/\/\S+)/g);

  return (
    <div
      className={cn(
        "flex flex-1 flex-col",
        messageStyle === "outgoing" ? "items-end" : "items-start",
      )}
    >
      <div
        className={cn(
          "inline-block whitespace-pre-line break-words rounded-3xl px-4 py-2",
          messageStyle === "outgoing"
            ? "bg-blue-600 text-white"
            : "bg-zinc-100 text-black",
          message.customType === "SUPERVISION" && "bg-red-400 text-white",
          isHighlighted && "!bg-[#FFF5F5] !text-black ring-2 ring-[#FF6B6B]",
        )}
      >
        {textParts.map((part, index) => {
          if (links.includes(part)) {
            return (
              <a
                key={index}
                href={part}
                target="_blank"
                rel="noreferrer"
                className={cn(
                  "mx-1 underline",
                  messageStyle === "outgoing" ? "text-white" : "text-blue-600",
                )}
              >
                {part}
              </a>
            );
          }

          return (
            <span key={index} className="inline-block">
              {part}
            </span>
          );
        })}
      </div>
      {isTranslated && (
        <button
          type="button"
          onClick={() => setShowOriginal((prev) => !prev)}
          className="mt-1 text-xs text-gray-500 hover:underline"
        >
          {showOriginal ? "Show translation" : "Translated · Show original"}
        </button>
      )}
    </div>
  );
}

function AttachmentMessage({
  message,
  messageStyle,
  onRequestTranscript,
}: {
  message: ChatMessage;
  messageStyle: "incoming" | "outgoing";
  onRequestTranscript?: () => void;
}) {
  const mimeType = message.attachment?.mimeType ?? "";
  const url = message.attachment?.url;

  if (!url) {
    if (message.attachmentPending) {
      return <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />;
    }
    return (
      <div
        className={cn(
          "rounded-3xl px-4 py-2 text-sm text-zinc-500",
          messageStyle === "outgoing" ? "bg-blue-600/20" : "bg-zinc-100",
        )}
      >
        Media unavailable
      </div>
    );
  }

  if (mimeType.includes("image")) {
    return (
      <HeicImage
        src={url}
        mimeType={mimeType}
        alt="message"
        className={cn(
          "rounded-lg",
          messageStyle === "outgoing"
            ? "bg-blue-600 text-white"
            : "bg-gray-300 text-gray-600",
        )}
      />
    );
  }

  if (mimeType.includes("audio")) {
    return (
      <AudioMessage
        url={url}
        transcription={message.transcription?.trim()}
        transcriptionPending={message.transcriptionPending}
        onRequestTranscript={onRequestTranscript}
      />
    );
  }

  if (mimeType.includes("video") || mimeType.includes("mov")) {
    return (
      <MutingVideo
        src={url}
        controls
        className={cn(
          "rounded-lg",
          messageStyle === "outgoing"
            ? "bg-blue-600 text-white"
            : "bg-gray-300 text-gray-600",
        )}
      />
    );
  }

  return null;
}

function AudioMessage({
  url,
  transcription,
  transcriptionPending,
  onRequestTranscript,
}: {
  url: string;
  transcription?: string;
  transcriptionPending?: boolean;
  onRequestTranscript?: () => void;
}) {
  const { transcodeToM4a } = useAudioTranscoder();
  const [src, setSrc] = useState(url);
  const [phase, setPhase] = useState<"raw" | "repairing" | "failed">("raw");
  const urlRef = useRef(url);
  const objectUrlRef = useRef<string | null>(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    urlRef.current = url;
    setSrc(url);
    setPhase("raw");
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [url]);

  const handleError = (event: SyntheticEvent<HTMLAudioElement>) => {
    const code = event.currentTarget.error?.code ?? null;
    if (!isUnsupportedAudio(code)) return;
    if (src !== url) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setPhase("failed");
      return;
    }
    const requestedUrl = url;
    setPhase("repairing");
    void loadPlayableAudio(requestedUrl, transcodeToM4a).then((blob) => {
      if (!aliveRef.current || urlRef.current !== requestedUrl || !blob) {
        if (aliveRef.current && urlRef.current === requestedUrl) {
          setPhase("failed");
        }
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      if (!aliveRef.current || urlRef.current !== requestedUrl) {
        URL.revokeObjectURL(objectUrl);
        return;
      }
      objectUrlRef.current = objectUrl;
      setSrc(objectUrl);
      setPhase("raw");
    });
  };

  return (
    <div className="flex flex-col gap-1.5">
      {phase === "failed" ? (
        <p className="text-sm text-zinc-500">Cannot play this audio</p>
      ) : phase === "repairing" ? (
        <p className="flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Preparing audio…
        </p>
      ) : (
        <MutingAudio
          src={src}
          controls
          className="rounded-lg"
          onError={handleError}
        />
      )}
      <AudioTranscript
        transcription={transcription}
        pending={transcriptionPending}
        onRequest={onRequestTranscript}
      />
    </div>
  );
}

function AudioTranscript({
  transcription,
  pending,
  onRequest,
}: {
  transcription?: string;
  pending?: boolean;
  onRequest?: () => void;
}) {
  const { track } = useTrackEvent();
  const hasTranscript = Boolean(transcription);

  const handleOpenChange = (open: boolean) => {
    if (!open) return;
    track(PostHogEvents.VOICE_NOTE_TRANSCRIPT_VIEWED, {
      had_transcript: hasTranscript,
    });
    if (!hasTranscript) {
      onRequest?.();
    }
  };

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="ml-2 flex w-fit items-center gap-1 text-xs font-medium text-zinc-500 transition-colors hover:text-zinc-700"
        >
          <CaptionsIcon className="h-3.5 w-3.5" />
          Show transcript
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-400">
          Transcript
        </p>
        {hasTranscript ? (
          <p className="max-h-48 overflow-y-auto whitespace-pre-line break-words text-sm text-zinc-700">
            {transcription}
          </p>
        ) : pending ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading...
          </div>
        ) : (
          <p className="text-sm text-zinc-500">No transcript available yet.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

const trackedTakedownViews = new Set<string>();

function TakedownNotice({
  takedown,
  messageId,
  conversationId,
  onReversed,
}: {
  takedown: MessageTakedownData;
  messageId: string | number;
  conversationId: string | null;
  onReversed: () => void;
}) {
  const { track } = useTrackEvent();
  const alertId = takedown.takedown_alert_id ?? undefined;

  useEffect(() => {
    const trackingKey = alertId ?? String(messageId);
    if (trackedTakedownViews.has(trackingKey)) return;
    trackedTakedownViews.add(trackingKey);
    track(PostHogEvents.MENTOR_CHAT_TAKEDOWN_VIEW, {
      conversation_id: conversationId,
      message_id: String(messageId),
      s_tier: takedown.takedown_s_tier,
    });
  }, [alertId, messageId, conversationId, track, takedown.takedown_s_tier]);

  const body =
    takedown.takedown_actor_role === "MENTOR"
      ? "This content was removed because it violated our safety policy."
      : "This message from your mentee was removed for a safety policy violation. Our team has been notified.";

  return (
    <ContentRemovedBadge body={body}>
      <TakedownActions
        alertId={alertId}
        onReverseClick={() =>
          track(PostHogEvents.MENTOR_CHAT_TAKEDOWN_REVERSE, {
            alert_id: alertId,
          })
        }
        onReversed={onReversed}
      />
    </ContentRemovedBadge>
  );
}

function RedactedMessage() {
  return (
    <div className="inline-block whitespace-pre-line break-words rounded-3xl bg-zinc-200 px-4 py-2 text-sm italic text-zinc-400">
      This message has been redacted
    </div>
  );
}

function QuotedMessageCard({
  message,
  onClick,
}: {
  message: ChatMessage;
  onClick?: () => void;
}) {
  return (
    <div
      className="-mb-4 flex cursor-pointer flex-row items-center justify-between gap-2 rounded-lg bg-gray-100 p-2 pb-4 active:bg-gray-200"
      onClick={onClick}
    >
      <div className="flex flex-grow flex-row items-center">
        <QuotedMessageContent message={message} />
      </div>
      <div className="flex items-center gap-2">
        <QuotedMessagePreview message={message} />
      </div>
    </div>
  );
}

function QuotedMessageContent({ message }: { message: ChatMessage }) {
  const mimeType = message.attachment?.mimeType ?? "";
  const isImage = mimeType.includes("image");
  const isAudio = mimeType.includes("audio");
  const isVideo = mimeType.includes("video");
  const isGif = message.customType === "GIF_MESSAGE";
  const { text: textContent } = getDisplayText(
    { message: message.text, translations: message.translations ?? {} },
    MENTOR_LANGUAGE,
  );

  return (
    <div className="flex max-w-md flex-col">
      <span className="text-sm font-semibold text-blue-500">
        {message.sender?.displayName ?? message.sender?.nickname ?? "Unknown"}
      </span>
      <div className="flex items-center gap-2">
        {isImage && <ImageIcon className="h-4 w-4 text-gray-500" />}
        {isAudio && <MicIcon className="h-4 w-4 text-gray-500" />}
        {isVideo && <VideoIcon className="h-4 w-4 text-gray-500" />}
        {isGif && <FileImageIcon className="h-4 w-4 text-gray-500" />}
        <span className="max-w-2xl truncate text-sm text-gray-700">
          {isImage && "Photo"}
          {isAudio && "Audio"}
          {isVideo && "Video"}
          {isGif && "GIF"}
          {!isImage && !isAudio && !isVideo && !isGif && textContent}
        </span>
      </div>
    </div>
  );
}

function QuotedMessagePreview({ message }: { message: ChatMessage }) {
  const mimeType = message.attachment?.mimeType ?? "";
  const isImage = mimeType.includes("image");
  const isAudio = mimeType.includes("audio");
  const isVideo = mimeType.includes("video");
  const isGif = message.customType === "GIF_MESSAGE";

  if (isGif) {
    return (
      <div className="h-10 w-10 overflow-hidden rounded">
        <img
          src={message.text}
          alt="GIF Preview"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  if (isImage && message.attachment?.url) {
    return (
      <div className="h-10 w-10 overflow-hidden rounded">
        <HeicImage
          src={message.attachment.url}
          mimeType={message.attachment.mimeType ?? undefined}
          alt="Preview"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  if (isImage) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
        <ImageIcon className="h-6 w-6 text-gray-500" />
      </div>
    );
  }

  if (isAudio) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
        <MicIcon className="h-6 w-6 text-gray-500" />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
        <VideoIcon className="h-6 w-6 text-gray-500" />
      </div>
    );
  }

  return null;
}
